import React, { useState } from 'react';
import { Box, Button, Divider, Fade, IconButton, Modal, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFieldArray } from 'react-hook-form';
import { ALLOWANCE, CHARGE } from '../../constants/defaults';
import AllowanceChargeSection from '../../components/form/AllowanceChargeSection';
import AddAllowanceChargeModal from '../../components/form/AddAllowanceChargeModal';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 800,
  bgcolor: 'background.paper',
  borderRadius: '15px',
  boxShadow: 24,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
};

const DocumentLevelChargeAndAllowanceModal = ({
  open,
  onClose,
  control,
  readOnly = false,
  currentNetAmount = 0,
  itemBaseAmount = 0,
}) => {
  const {
    fields: allowanceFields,
    append: appendAllowance,
    remove: removeAllowance,
    update: updateAllowance,
  } = useFieldArray({
    control,
    name: 'allowances',
  });

  const {
    fields: chargeFields,
    append: appendCharge,
    remove: removeCharge,
    update: updateCharge,
  } = useFieldArray({
    control,
    name: 'charges',
  });

  // Modal state
  const [allowanceModalOpen, setAllowanceModalOpen] = useState(false);
  const [allowanceType, setAllowanceType] = useState(ALLOWANCE);
  const [editingAllowanceIndex, setEditingAllowanceIndex] = useState();

  const handleAddAllowance = (allowance) => {
    if (allowanceType === ALLOWANCE) {
      if (editingAllowanceIndex !== undefined) {
        updateAllowance(editingAllowanceIndex, allowance);
        setEditingAllowanceIndex(undefined);
      } else {
        appendAllowance(allowance);
      }
    } else {
      if (editingAllowanceIndex !== undefined) {
        updateCharge(editingAllowanceIndex, allowance);
        setEditingAllowanceIndex(undefined);
      } else {
        appendCharge(allowance);
      }
    }
  };

  const handleOpenDiscountModal = () => {
    setAllowanceType(ALLOWANCE);
    setEditingAllowanceIndex(undefined);
    setAllowanceModalOpen(true);
  };

  const handleOpenChargeModal = () => {
    setAllowanceType(CHARGE);
    setEditingAllowanceIndex(undefined);
    setAllowanceModalOpen(true);
  };

  const handleEditAllowance = (index, type) => {
    setAllowanceType(type);
    setEditingAllowanceIndex(index);
    setAllowanceModalOpen(true);
  };

  const handleDeleteAllowance = (index, type) => {
    if (type === ALLOWANCE) {
      removeAllowance(index);
    } else {
      removeCharge(index);
    }
  };

  const getEditingData = () => {
    if (editingAllowanceIndex === undefined) return undefined;

    if (allowanceType === ALLOWANCE) {
      return allowanceFields[editingAllowanceIndex];
    } else {
      return chargeFields[editingAllowanceIndex];
    }
  };

  return (
    <>
      <Modal open={open} onClose={(_, reason) => reason !== 'backdropClick' && onClose()} className="backdrop-model">
        <Fade in={open}>
          <Box sx={modalStyle}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ p: 2, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
            >
              <Typography variant="h6">Document Level Charges And Allowances</Typography>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Divider />

            <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
              <AllowanceChargeSection
                data-testid="charges-section"
                title="Charges"
                type={CHARGE}
                fields={chargeFields}
                onAdd={handleOpenChargeModal}
                onEdit={handleEditAllowance}
                onDelete={handleDeleteAllowance}
                readOnly={readOnly}
                isDocumentLevel={true}
              />

              <AllowanceChargeSection
                data-testid="allowances-section"
                title="Allowances (Discounts)"
                type={ALLOWANCE}
                fields={allowanceFields}
                onAdd={handleOpenDiscountModal}
                onEdit={handleEditAllowance}
                onDelete={handleDeleteAllowance}
                readOnly={readOnly}
                isDocumentLevel={true}
              />
            </Box>

            <Divider />

            {/* Footer */}
            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1}
              sx={{ px: 2, py: 2, borderTop: (t) => `1px solid ${t.palette.divider}` }}
            >
              <Button variant="contained" onClick={onClose}>
                Done
              </Button>
            </Stack>
          </Box>
        </Fade>
      </Modal>

      {/* Add/Edit Allowance Modal */}
      <AddAllowanceChargeModal
        open={allowanceModalOpen}
        onClose={() => setAllowanceModalOpen(false)}
        onAdd={handleAddAllowance}
        type={allowanceType}
        initialFormData={getEditingData()}
        editIndex={editingAllowanceIndex}
        currentNetAmount={currentNetAmount}
        itemBaseAmount={itemBaseAmount}
        isDocumentLevel={true}
      />
    </>
  );
};

export default DocumentLevelChargeAndAllowanceModal;
